import { SelectOption as Option } from 'components/select/types';
import { useMemo } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { Gender } from 'models/genders';
import { useAppStore } from 'hooks/state/useAppStore';
import { useTransitiveCacheStore } from 'hooks/state/useTransitiveCacheStore';
import { BaseSelect } from '../../BaseSelect';

interface GenderSelectProps {
  selectedGenders?: string[];
  setSelectedGenders?: (value: string[]) => void;
  className?: string;
  showConfirmButton?: boolean;
}

const mapGenderToGenderOption = (gender: Gender, intl: IntlShape): Option<string> => ({
  value: gender.genderCode,
  label: intl.formatMessage({
    id: `product.genders.${gender.genderCode}`,
    defaultMessage: gender.genderCode,
  }),
});

export function GenderSelect({
  selectedGenders = undefined,
  setSelectedGenders = undefined,
  className = undefined,
  showConfirmButton = false,
}: GenderSelectProps) {
  const intl = useIntl();

  const { allGenders, disabledFilters } = useTransitiveCacheStore();
  const allGenderOptions = useMemo(
    () =>
      allGenders
        .map(c => mapGenderToGenderOption(c, intl))
        .sort((a, b) => {
          if (a.label && b.label) {
            return a.label.localeCompare(b.label);
          }

          return 0;
        }),
    [allGenders, intl]
  );

  const { genders, setGenders } = useAppStore();

  return (
    <BaseSelect
      disabled={disabledFilters.includes('prdGender')}
      id="gender-filter"
      className={className}
      label={intl.formatMessage({
        id: 'filter.key.selectedGenders',
        defaultMessage: 'Genders',
      })}
      selectedItems={selectedGenders ?? genders ?? []}
      allOptionItems={allGenderOptions}
      setSelectedItems={setSelectedGenders ?? setGenders}
      showConfirmButton={showConfirmButton}
    />
  );
}
